import React, { useCallback, useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import LinearProgress from "@mui/material/LinearProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Alert from "@mui/material/Alert";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import ReplayIcon from "@mui/icons-material/Replay";
import ScienceIcon from "@mui/icons-material/Science";
import { useAuth } from "../../../context/AuthContext";
import { DEEP_TEST_STEPS, runDeepDatabaseTest } from "../services/connectionDeepTestService";
import {
  exportDeepTestCsv,
  exportDeepTestJson,
  exportDeepTestPdf,
  formatDeepTestReport,
} from "../services/connectionDeepTestExport";

const TAB_LABELS = [
  "Summary",
  "Steps",
  "Permissions",
  "Performance",
  "Collections",
  "Database",
  "Warnings",
  "Report",
  "Raw JSON",
];

function stepStatusColor(status) {
  if (status === "success") return "success";
  if (status === "skipped" || status === "not_configured") return "default";
  if (status === "read_only" || status === "warning" || status === "denied") return "warning";
  if (status === "failed") return "error";
  return "default";
}

function stepIcon(status) {
  if (status === "success") return "✔";
  if (status === "skipped") return "○";
  if (status === "failed") return "✘";
  if (status === "running") return "…";
  return "⚠";
}

function overallColor(overall) {
  if (overall === "passed") return "success";
  if (overall === "warning") return "warning";
  return "error";
}

function scoreColor(score) {
  if (score >= 90) return "success";
  if (score >= 70) return "warning";
  return "error";
}

function downloadText(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function ScoreCard({ label, value, unit = "%" }) {
  return (
    <Box sx={{ minWidth: 120 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={800} color={`${scoreColor(value)}.main`}>
        {value}
        <Typography component="span" variant="body2" color="text.secondary">{unit}</Typography>
      </Typography>
    </Box>
  );
}

export default function ConnectionDeepTestDialog({ open, connection, onClose }) {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ value: 0, label: "" });
  const [liveSteps, setLiveSteps] = useState([]);
  const [report, setReport] = useState(null);
  const [copied, setCopied] = useState(false);
  const runningRef = useRef(false);

  const baseName = connection?.name?.replace(/\s+/g, "-") || "connection";
  const verifiedBy = user?.email || user?.username || user?.uid || "";

  const runTest = useCallback(async () => {
    if (!connection || runningRef.current) return;
    runningRef.current = true;
    setRunning(true);
    setReport(null);
    setLiveSteps([]);
    setProgress({ value: 0, label: "Starting…" });
    setTab(0);
    try {
      const result = await runDeepDatabaseTest(connection.id, {
        verifiedBy,
        onProgress: (p) => {
          setProgress({ value: p.progress || 0, label: p.label || "" });
          if (Array.isArray(p.steps)) setLiveSteps(p.steps);
        },
      });
      setReport(result);
      setProgress({ value: 100, label: "Complete" });
    } catch (e) {
      setProgress({ value: 100, label: e?.message || "Deep test failed" });
    } finally {
      runningRef.current = false;
      setRunning(false);
    }
  }, [connection, verifiedBy]);

  useEffect(() => {
    if (open && connection && !runningRef.current && !report) {
      runTest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, connection]);

  const handleClose = () => {
    setReport(null);
    setLiveSteps([]);
    setProgress({ value: 0, label: "" });
    setTab(0);
    onClose?.();
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(formatDeepTestReport(report));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportJson = () =>
    downloadText(`${baseName}-deep-test.json`, exportDeepTestJson(report), "application/json");
  const handleExportCsv = () =>
    downloadText(`${baseName}-deep-test.csv`, exportDeepTestCsv(report), "text/csv");
  const handleExportPdf = async () => {
    await exportDeepTestPdf(report, `${baseName}-deep-test.pdf`);
  };

  const stepsToRender = report?.steps || liveSteps;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <ScienceIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>
            Firestore Deep Test Suite
          </Typography>
          {report && (
            <Chip size="small" variant="outlined" label={`SDK ${report.advanced?.sdkVersion || "12.x"}`} />
          )}
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {connection && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {connection.name} · <code>{connection.firebaseProjectId}</code>
          </Typography>
        )}

        {/* Live progress */}
        {(running || !report) && (
          <Box sx={{ py: 2 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>{progress.label || "Preparing…"}</Typography>
              <Typography variant="body2" color="text.secondary">{progress.value}%</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={progress.value} sx={{ height: 8, borderRadius: 1 }} />
            <Stack spacing={0.5} sx={{ mt: 2 }}>
              {DEEP_TEST_STEPS.map((def) => {
                const done = stepsToRender.find((s) => s.id === def.id);
                const isCurrent = running && progress.label === def.label && !done;
                return (
                  <Stack key={def.id} direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ minWidth: 24 }}>
                      {done ? stepIcon(done.status) : isCurrent ? "…" : "•"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={done ? "text.primary" : isCurrent ? "primary.main" : "text.disabled"}
                      sx={{ flex: 1 }}
                    >
                      {def.label}
                    </Typography>
                    {done && (
                      <Chip size="small" label={done.status} color={stepStatusColor(done.status)} variant="outlined" />
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Report */}
        {!running && report && (
          <Box>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip label={report.overall} color={overallColor(report.overall)} sx={{ textTransform: "uppercase", fontWeight: 700 }} />
              <Typography variant="body2" color="text.secondary">
                {report.duration} ms · {report.advanced?.browser} · {report.advanced?.os}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mb: 2 }}>
              <ScoreCard label="Health" value={report.healthScore} />
              <ScoreCard label="Performance" value={report.performanceScore} />
              <ScoreCard label="Permission" value={report.permissionScore} />
              <ScoreCard label="Security" value={report.securityScore} />
            </Stack>

            <Alert severity={report.overall === "failed" ? "error" : report.overall === "warning" ? "warning" : "success"} sx={{ mb: 2 }}>
              {report.recommendation}
            </Alert>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
              {TAB_LABELS.map((label) => <Tab key={label} label={label} />)}
            </Tabs>

            {/* Summary */}
            <TabPanel value={tab} index={0}>
              <List dense disablePadding>
                {report.steps.map((s) => (
                  <ListItem key={s.id} disableGutters sx={{ alignItems: "flex-start" }}>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="body2" fontWeight={600} sx={{ minWidth: 200 }}>
                            {stepIcon(s.status)} {s.label}
                          </Typography>
                          <Chip size="small" label={s.status} color={stepStatusColor(s.status)} />
                          {s.latencyMs > 0 && (
                            <Typography variant="caption" color="text.secondary">{s.latencyMs} ms</Typography>
                          )}
                        </Stack>
                      }
                      secondary={s.message}
                    />
                  </ListItem>
                ))}
              </List>
            </TabPanel>

            {/* Steps (table) */}
            <TabPanel value={tab} index={1}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Step</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Latency</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.steps.map((s, i) => (
                    <TableRow key={s.id}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{s.label}</TableCell>
                      <TableCell><Chip size="small" label={s.status} color={stepStatusColor(s.status)} /></TableCell>
                      <TableCell>{s.latencyMs ? `${s.latencyMs} ms` : "—"}</TableCell>
                      <TableCell>{s.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabPanel>

            {/* Permissions */}
            <TabPanel value={tab} index={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Operation</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(report.permissions).map(([k, v]) => (
                    <TableRow key={k}>
                      <TableCell sx={{ textTransform: "capitalize" }}>{k}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={v.status === "allowed" ? "Allowed" : v.status === "denied" ? "Denied" : v.status}
                          color={v.status === "allowed" ? "success" : v.status === "denied" ? "error" : "default"}
                        />
                      </TableCell>
                      <TableCell>{v.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabPanel>

            {/* Performance */}
            <TabPanel value={tab} index={3}>
              <Stack spacing={1}>
                {["init", "authentication", "read", "write", "update", "delete", "batch", "transaction", "average", "minimum", "maximum"].map((key) => (
                  <Stack key={key} direction="row" justifyContent="space-between">
                    <Typography variant="body2" textTransform="capitalize">{key}</Typography>
                    <Typography variant="body2" fontWeight={600}>{report.performance?.[key] ?? 0} ms</Typography>
                  </Stack>
                ))}
              </Stack>
            </TabPanel>

            {/* Collections */}
            <TabPanel value={tab} index={4}>
              {report.collections ? (
                <Stack spacing={2}>
                  <Stack direction="row" spacing={3} flexWrap="wrap">
                    <Box>
                      <Typography variant="caption" color="text.secondary">Accessible Collections</Typography>
                      <Typography variant="h6">{report.collections.totalAccessible}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Sample Documents</Typography>
                      <Typography variant="h6">{report.collections.sampleDocuments}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Largest Collection</Typography>
                      <Typography variant="h6">{report.collections.largestCollection} ({report.collections.largestCount})</Typography>
                    </Box>
                  </Stack>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Collection</TableCell>
                        <TableCell>Sampled Docs</TableCell>
                        <TableCell>Est. Bytes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(report.collections.collections || []).map((c) => (
                        <TableRow key={c.name}>
                          <TableCell><code>{c.name}</code></TableCell>
                          <TableCell>{c.count}</TableCell>
                          <TableCell>{c.estimatedBytes}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Stack>
              ) : (
                <Typography color="text.secondary">Collection discovery not available.</Typography>
              )}
            </TabPanel>

            {/* Database */}
            <TabPanel value={tab} index={5}>
              <Table size="small">
                <TableBody>
                  {Object.entries(report.database || {}).map(([k, v]) => (
                    <TableRow key={k}>
                      <TableCell sx={{ textTransform: "capitalize", width: 200 }}>{k.replace(/([A-Z])/g, " $1")}</TableCell>
                      <TableCell><code>{String(v)}</code></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabPanel>

            {/* Warnings */}
            <TabPanel value={tab} index={6}>
              {report.warnings?.length ? (
                <List dense>
                  {report.warnings.map((w, i) => (
                    <ListItem key={i} divider>
                      <ListItemText primary={w.message} secondary={w.type} />
                      <Chip size="small" label={w.severity} color={w.severity === "error" ? "error" : "warning"} variant="outlined" />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">No warnings detected.</Typography>
              )}
            </TabPanel>

            {/* Report (text) */}
            <TabPanel value={tab} index={7}>
              <Typography
                component="pre"
                sx={{ fontSize: 12, bgcolor: "action.hover", p: 2, borderRadius: 1, overflow: "auto", maxHeight: 420, whiteSpace: "pre-wrap" }}
              >
                {formatDeepTestReport(report)}
              </Typography>
            </TabPanel>

            {/* Raw JSON */}
            <TabPanel value={tab} index={8}>
              <Typography
                component="pre"
                sx={{ fontSize: 11, bgcolor: "action.hover", p: 2, borderRadius: 1, overflow: "auto", maxHeight: 420 }}
              >
                {exportDeepTestJson(report)}
              </Typography>
            </TabPanel>

            {report.completedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
                Completed at {new Date(report.completedAt).toLocaleString()}
                {report.advanced?.verifiedBy && report.advanced.verifiedBy !== "—" ? ` by ${report.advanced.verifiedBy}` : ""}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 2, py: 1.5 }}>
        {!running && report && (
          <>
            <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleCopy}>
              {copied ? "Copied" : "Copy Report"}
            </Button>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleExportJson}>JSON</Button>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleExportCsv}>CSV</Button>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleExportPdf}>PDF</Button>
            <Button size="small" startIcon={<ReplayIcon />} onClick={runTest}>Re-run</Button>
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose} disabled={running}>{running ? "Running…" : "Close"}</Button>
      </DialogActions>
    </Dialog>
  );
}
